import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'

import { useUpdateQuoteItemMutation } from '@/app/services/quoteItems'
import { quotesApi, useGetQuoteQuery } from '@/app/services/quotes'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { addItemToUpdateQueue, removeItemFromUpdateQueue, setSelectedQuoteStatus } from '@/app/slices/quoteItemsSlice'
import { appInsights } from '@/common/utils/ApplicationInsights/ApplicationInsights'
import { QUOTE_STATUS_ARRAY } from '@/common/utils/Constants/Constants'

/**
 * Updates above this duration (ms) are reported to Application Insights so we can
 * surface backend slowness that leaves the Calculate button disabled for too long.
 * @type {number}
 */
const SLOW_UPDATE_THRESHOLD_MS = 10000

/**
 * Emits an Application Insights signal describing the outcome of a quote-item update.
 *
 * Failures are reported as exceptions; slow successes as a named event. Both carry the
 * material/thickness/duration context needed to triage QM-3441 (Calculate button stuck disabled).
 *
 * @param {Object} params
 * @param {'success'|'error'} params.outcome - Whether the update resolved or rejected.
 * @param {Object} params.quoteItem - The quote item that was being updated.
 * @param {string} params.quoteId - The owning quote id.
 * @param {number} params.durationMs - How long the awaited mutation took.
 * @param {unknown} [params.error] - The error thrown when `outcome` is `'error'`.
 * @returns {void}
 */
const trackQuoteItemUpdate = ({ durationMs, error, outcome, quoteId, quoteItem }) => {
    if (!appInsights) return

    const properties = {
        feature: 'quote-item-update',
        quoteId,
        quoteItemId: quoteItem?.id,
        materialId: quoteItem?.materialId,
        thickness: quoteItem?.thickness,
        cuttingTechnologyId: quoteItem?.cuttingTechnologyId,
        durationMs: Math.round(durationMs),
    }

    if (outcome === 'error') {
        appInsights.trackException({ exception: error, properties })
        return
    }

    if (durationMs >= SLOW_UPDATE_THRESHOLD_MS) {
        appInsights.trackEvent({ name: 'quote-item-update-slow', properties })
    }
}

/**
 * useUpdateQuoteItem - Owns the full update lifecycle for a quote item.
 *
 * Centralises the previously-duplicated `handleUpdateQuoteItem` / `handleUpdateQuote` logic from the
 * item editor components (settings, folding, secondary processes). The Calculate button is gated on an
 * empty `updateQueue`; this hook guarantees the item is removed from the queue on **every** outcome
 * (success, error, or timeout) via a `finally` block, so a failed update can never leave the button
 * permanently disabled (QM-3441). It also reports failed/slow updates to Application Insights.
 *
 * Import directly (no barrel): `import { useUpdateQuoteItem } from '@/common/hooks/useUpdateQuoteItem'`.
 *
 * @returns {{
 *   markItemPending: (quoteItemId: string) => void,
 *   handleUpdateQuoteItem: (quoteItem: Object) => Promise<void>,
 * }} Queue/update helpers for editor components.
 */
export const useUpdateQuoteItem = () => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { quoteId } = useParams()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)

    const { data: selectedQuote } = useGetQuoteQuery({ organisationId, quoteId })

    // Share mutation loading state with ActionsButtonGroup via this fixed cache key.
    const [updateQuoteItem] = useUpdateQuoteItemMutation({ fixedCacheKey: 'shared-quote-item-update' })

    /**
     * Marks an item as pending so the Calculate button disables immediately on edit, before the
     * (often debounced) network update runs.
     *
     * @param {string} quoteItemId - The quote item being edited.
     * @returns {void}
     */
    const markItemPending = useCallback(
        (quoteItemId) => {
            dispatch(addItemToUpdateQueue(quoteItemId))
            if (selectedQuote?.status !== QUOTE_STATUS_ARRAY.NotCalculated) {
                dispatch(setSelectedQuoteStatus(QUOTE_STATUS_ARRAY.NotCalculated))
            }
        },
        [dispatch, selectedQuote?.status]
    )

    /**
     * Reflects the quote's NotCalculated status in the getQuote cache.
     *
     * Editing an item invalidates the quote's calculated prices. The item-update endpoint already
     * persists `status = NotCalculated` on the server (UpdateQuoteItemFromDtoV2), so this only syncs
     * the local cache — it does NOT fire a second updateQuote PUT. That redundant PUT used to run
     * concurrently with the item save and race it on the same quote, causing DbUpdateConcurrencyException.
     *
     * @returns {void}
     */
    const reflectQuoteNotCalculatedInCache = useCallback(() => {
        dispatch(
            quotesApi.util.updateQueryData('getQuote', { organisationId, quoteId }, (draft) => {
                if (draft) {
                    draft.status = QUOTE_STATUS_ARRAY.NotCalculated
                }
            })
        )
    }, [dispatch, organisationId, quoteId])

    /**
     * Persists a quote item update and reconciles the update queue on every outcome.
     *
     * @param {Object} quoteItem - The full quote item payload to persist (must include `id`).
     * @returns {Promise<void>}
     */
    const handleUpdateQuoteItem = useCallback(
        async (quoteItem) => {
            const quoteItemId = quoteItem?.id
            const startedAt = performance.now()

            try {
                await updateQuoteItem({ organisationId, quoteId, quoteItemId, quoteItem }).unwrap()

                if (selectedQuote?.status !== QUOTE_STATUS_ARRAY.NotCalculated) {
                    reflectQuoteNotCalculatedInCache()
                }

                trackQuoteItemUpdate({
                    outcome: 'success',
                    quoteItem,
                    quoteId,
                    durationMs: performance.now() - startedAt,
                })
            } catch (error) {
                enqueueSnackbar(t('Failed to update the $t(quote) item'), { variant: 'error' })
                trackQuoteItemUpdate({
                    outcome: 'error',
                    quoteItem,
                    quoteId,
                    error,
                    durationMs: performance.now() - startedAt,
                })
            } finally {
                // The Calculate button is gated on an empty queue — always reconcile, even on failure.
                dispatch(removeItemFromUpdateQueue(quoteItemId))
            }
        },
        [
            dispatch,
            enqueueSnackbar,
            organisationId,
            quoteId,
            reflectQuoteNotCalculatedInCache,
            selectedQuote?.status,
            t,
            updateQuoteItem,
        ]
    )

    return { markItemPending, handleUpdateQuoteItem }
}
